import '../../../../../profint.shared10/src/Profint.Shared.UI/wwwroot/js/profint-dialogs.js';
import { enhanceSharedDates } from './shared-dates';
import './shared-fields';
import { mountFilePicker } from '../../../../../profint.shared10/src/Profint.Shared.UI/assets/file-picker';
import './styles/main.scss';
import 'bootstrap';
import 'htmx.org';
import QRCode from 'qrcode';

import '@phosphor-icons/web/regular';
import '@phosphor-icons/web/fill';
import '@phosphor-icons/web/duotone';
import './global-loading';
import './profint-clinical-assistant';
import '../../../../../profint.shared10/src/Profint.Shared.UI/wwwroot/styles/profint-daterange.css';
import '../../../../../profint.shared10/src/Profint.Shared.UI/wwwroot/js/profint-daterange.js';

function setFiles(target: HTMLInputElement, file: File): void {
  const transfer = new DataTransfer();
  transfer.items.add(file);
  target.files = transfer.files;
}

function initClinicalAttachments(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLFormElement>('[data-clinical-attachment-upload]').forEach((form) => {
    if (form.dataset.clinicalAttachmentsReady === 'true') return;
    form.dataset.clinicalAttachmentsReady = 'true';

    const fileInput = form.querySelector<HTMLInputElement>('[data-clinical-file-input]');
    const cameraInput = form.querySelector<HTMLInputElement>('[data-clinical-camera-input]');
    const preview = form.querySelector<HTMLElement>('[data-clinical-attachment-preview]');
    const fileName = form.querySelector<HTMLElement>('[data-clinical-file-name]');
    const qrToggle = form.querySelector<HTMLButtonElement>('[data-clinical-qr-toggle]');
    const qrPanel = form.querySelector<HTMLElement>('[data-clinical-qr-panel]');
    const qrCanvas = form.querySelector<HTMLCanvasElement>('[data-clinical-qr-canvas]');
    const mobileUrl = qrCanvas?.dataset.clinicalMobileUrl;

    const paintPreview = (file: File | null) => {
      if (!file || !preview || !fileName) return;
      fileName.textContent = `${file.name} · ${Math.max(1, Math.ceil(file.size / 1024))} KB`;
      preview.hidden = false;
    };

    fileInput?.addEventListener('change', () => paintPreview(fileInput.files?.[0] ?? null));
    cameraInput?.addEventListener('change', () => {
      const file = cameraInput.files?.[0] ?? null;
      if (file && fileInput) setFiles(fileInput, file);
      paintPreview(file);
    });

    qrToggle?.addEventListener('click', () => {
      if (!qrPanel) return;
      qrPanel.hidden = !qrPanel.hidden;
      if (!qrPanel.hidden && qrCanvas && mobileUrl && qrCanvas.dataset.rendered !== 'true') {
        qrCanvas.dataset.rendered = 'true';
        void QRCode.toCanvas(qrCanvas, mobileUrl, {
          width: 132,
          margin: 1,
          color: { dark: '#0f6d91', light: '#ffffff' },
        });
      }
    });
  });
}

initClinicalAttachments();

// ----------------------------------------------------------------------------
// Sidebar collapse — persiste en localStorage. Default desktop=expandido,
// mobile=colapsado (driven por viewport). Toggle desde el boton de la marca.
// ----------------------------------------------------------------------------
(function initSidebarCollapse() {
  const KEY = 'profint_sidebar_collapsed';
  const root = document.querySelector<HTMLElement>('.app, .rx-app');
  if (!root) return;

  let stored: string | null = null;
  try { stored = localStorage.getItem(KEY); } catch { /* Puede estar bloqueado por el navegador. */ }
  const initial = stored === null
    ? !window.matchMedia('(min-width: 992px)').matches
    : stored === '1';
  root.dataset.sidebarCollapsed = initial ? 'true' : 'false';

  document.querySelectorAll<HTMLButtonElement>('[data-toggle-sidebar]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const current = root.dataset.sidebarCollapsed === 'true';
      const next = !current;
      root.dataset.sidebarCollapsed = next ? 'true' : 'false';
      try {
        localStorage.setItem(KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
    });
  });
})();

// ----------------------------------------------------------------------------
// Submit spinner — para botones submit que dispararían un POST con I/O lento
// (mail, gateway REST). Atributo opt-in:
//
//   <button type="submit"
//           data-submit-spinner
//           data-spinner-icon="ph-paper-plane-tilt"
//           data-spinner-label="Enviando…">
//     Enviar instrucciones <i class="ph ph-paper-plane-tilt"></i>
//   </button>
//
// Al submit del form: el botón se deshabilita, el contenido se reemplaza por
// <span class="spinner" /> + label, y todos los inputs del form quedan
// readonly para evitar dobles posts. Si el server responde con error y
// vuelve a renderizar la página, el form arranca limpio (server-rendered).
// ----------------------------------------------------------------------------
// Submit progress and restoration are owned by shared-operation-controls.

// ----------------------------------------------------------------------------
// Theme toggle (claro/oscuro). Estado en localStorage.profint_theme (default
// 'light'). El tema oscuro se aplica como clase `dark` en <html> (el script
// inline del layout la setea temprano para evitar flash). Los controles
// [data-theme-toggle] reflejan el estado con la clase `.light`.
// ----------------------------------------------------------------------------
(function initThemeToggle() {
  const KEY = 'profint_theme';
  const read = (): 'light' | 'dark' => {
    try { return localStorage.getItem(KEY) === 'dark' ? 'dark' : 'light'; } catch { return 'light'; }
  };
  const apply = (theme: 'light' | 'dark') => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.querySelectorAll<HTMLElement>('[data-theme-toggle]').forEach((el) => {
      el.classList.toggle('light', theme === 'light');
    });
  };

  apply(read());

  document.querySelectorAll<HTMLButtonElement>('[data-theme-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const next = read() === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem(KEY, next); } catch { /* ignore */ }
      apply(next);
    });
  });
})();

// ----------------------------------------------------------------------------
// Controles modernos — REGLA del sistema: nada de <select> nativo en el portal.
// Cada `select.rx-select` se progresivamente-mejora en un dropdown custom glass:
// el <select> queda oculto (sr-only, submittable) y un trigger + menú lo manejan.
// Al elegir una opción se dispara `change` en el <select> → form.submit / htmx /
// listfilter siguen funcionando sin cambios.
// ----------------------------------------------------------------------------
function rxControlLabel(input: HTMLInputElement | HTMLSelectElement): string {
  return input.getAttribute('aria-label') || Array.from(input.labels ?? []).map((label) => label.textContent?.trim()).filter(Boolean).join(' ') || input.dataset.label || input.name;
}

function rxElements<T extends Element>(scope: ParentNode, selector: string): T[] {
  const elements = Array.from(scope.querySelectorAll<T>(selector));
  if (scope instanceof Element && scope.matches(selector)) elements.unshift(scope as T);
  return elements;
}

function connectRxControl(input: HTMLInputElement | HTMLSelectElement, trigger: HTMLButtonElement, wrap: HTMLElement) {
  input.tabIndex = -1;
  trigger.setAttribute('aria-label', rxControlLabel(input));
  const describedBy = input.getAttribute('aria-describedby');
  if (describedBy) trigger.setAttribute('aria-describedby', describedBy);
  input.labels?.forEach((label) => label.addEventListener('click', (event) => {
    if ((event.target as HTMLElement).closest('a, button')) return;
    event.preventDefault();
    trigger.focus();
  }));
  const error = document.createElement('span');
  error.className = 'rx-control-error';
  error.hidden = true;
  error.setAttribute('aria-live', 'polite');
  wrap.appendChild(error);
  input.addEventListener('invalid', (event) => {
    event.preventDefault();
    error.textContent = input.validationMessage;
    error.hidden = false;
    trigger.setAttribute('aria-invalid', 'true');
    trigger.focus();
  });
  input.addEventListener('change', () => {
    error.hidden = true;
    trigger.removeAttribute('aria-invalid');
  });
  input.form?.addEventListener('reset', () => {
    error.hidden = true;
    trigger.removeAttribute('aria-invalid');
  });
}

function enhanceRxSelects(scope: ParentNode = document) {
  const fields = (window as any).ProfintFields;
  if (!fields) return;
  rxElements<HTMLSelectElement>(scope, 'select.rx-select').forEach(select => {
    if (select.closest('[data-no-shared-fields]')) return;
    fields.createSelect(select, { maxOptions: null, placeholder: select.dataset.placeholder });
    select.dataset.rxEnhanced = 'shared';
  });
}

enhanceRxSelects();
// Re-enhance tras swaps htmx (parciales que traen selects nuevos).
document.body.addEventListener('htmx:afterSwap', (ev) => {
  const t = (ev as CustomEvent).detail?.target as ParentNode | undefined;
  enhanceRxSelects(t ?? document);
  enhanceRxDates(t ?? document);
  enhanceRxMonths(t ?? document);
  enhanceRxFiles(t ?? document);
  initClinicalAttachments(t ?? document);
});

// ----------------------------------------------------------------------------
// Control moderno — multiselect custom. Reemplaza `<select multiple>` nativo:
// trigger (cuenta seleccionada) + menú glass con búsqueda + checkboxes + chips.
// El <select multiple> queda oculto y submittable; cada toggle dispara `change`.
// ----------------------------------------------------------------------------



// ----------------------------------------------------------------------------
// Control moderno — date-picker custom (calendario). Reemplaza <input type=date>:
// el input queda oculto (value yyyy-MM-dd, submittable) y un campo + calendario
// glass lo manejan. Al elegir un día se dispara `change` (htmx/Alpine siguen ok).
// ----------------------------------------------------------------------------
function enhanceRxDates(scope: ParentNode = document) {
  enhanceSharedDates(scope);
}

enhanceRxDates();

// ----------------------------------------------------------------------------
// Month-range picker (.rx-monthrange) — período MM/AAAA, control custom (ley rx-).
// Reemplaza el <input type="month"> nativo. Dentro del contenedor van dos hidden
// inputs (data-role="from"/"to") que postean "MM/AAAA"; el JS arma el selector
// glass (dos columnas Desde/Hasta, nav por año + grilla de 12 meses).
// ----------------------------------------------------------------------------
function enhanceRxMonths(scope: ParentNode = document) {
  rxElements<HTMLElement>(scope, '.rx-monthrange').forEach(element => {
    (window as any).ProfintTemporal?.enhanceMonthRange(element);
  });
}

enhanceRxMonths();

// Los adjuntos conservan el input submittable y delegan en el control compartido.
function enhanceRxFiles(scope: ParentNode = document) {
  rxElements<HTMLInputElement>(scope, 'input[type="file"].rx-file').forEach((input) => {
    if (input.dataset.rxEnhanced) return;
    if (mountFilePicker(input)) input.dataset.rxEnhanced = '1';
  });
}
enhanceRxFiles();

document.addEventListener('profint:enhance-controls', (event) => {
  const scope = (event as CustomEvent).detail?.target as ParentNode | undefined;
  enhanceRxSelects(scope ?? document);
  enhanceRxDates(scope ?? document);
  enhanceRxMonths(scope ?? document);
  enhanceRxFiles(scope ?? document);
});

// ----------------------------------------------------------------------------
// Tabs client-side (.rx-pnav style) — sin dependencias. El servidor renderiza
// el panel activo (clase .active); este enhancer permite cambiar de tab sin
// recargar. Markup: contenedor [data-rx-tabs] con botones [data-rx-tab="x"] y
// paneles [data-rx-panel="x"]. Usado por "Mi perfil".
// ----------------------------------------------------------------------------
function initRxTabs(scope: ParentNode = document) {
  scope.querySelectorAll<HTMLElement>('[data-rx-tabs]').forEach((root) => {
    if (root.dataset.rxTabsInit) return;
    root.dataset.rxTabsInit = '1';
    const tabs = [...root.querySelectorAll<HTMLElement>('[data-rx-tab]')];
    const panels = [...root.querySelectorAll<HTMLElement>('[data-rx-panel]')];
    const activate = (id: string) => {
      tabs.forEach((t) => t.classList.toggle('active', t.dataset.rxTab === id));
      panels.forEach((p) => p.classList.toggle('active', p.dataset.rxPanel === id));
    };
    tabs.forEach((t) => t.addEventListener('click', () => {
      const id = t.dataset.rxTab;
      if (id) activate(id);
    }));
  });
}
initRxTabs();

// ----------------------------------------------------------------------------
// Documentos — previsualizador inline para PDFs/imágenes con descarga fallback.
// ----------------------------------------------------------------------------
(function initDocumentoPreview() {
  let revision = 0;
  let controller: AbortController | null = null;
  document.addEventListener('click', async event => {
    const button = (event.target as Element).closest<HTMLElement>('[data-doc-preview-url]');
    if (!button) return;
    event.preventDefault();
    controller?.abort(); controller = new AbortController();
    const version = ++revision;
    try {
      const url = new URL(button.dataset.docPreviewUrl || '', location.href);
      if (url.origin !== location.origin || !['http:', 'https:'].includes(url.protocol)) throw new Error('El documento debe provenir del mismo sitio.');
      const response = await fetch(url.href, { method: 'HEAD', credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('No se pudo cargar el archivo. Probá nuevamente o consultá a soporte.');
      if (version !== revision || !button.isConnected) return;
      const type = response.headers.get('content-type') || button.dataset.docContentType || (button.dataset.docPreviewKind === 'pdf' ? 'application/pdf' : '');
      if (button.dataset.docPreviewKind === 'pdf' && !type.toLowerCase().includes('pdf')) throw new Error('El servicio no devolvió el documento esperado.');
      const viewer = (window as any).ProfintDocumentPreview;
      if (!viewer) throw new Error('No se pudo cargar el visor. Recargá la página.');
      viewer.open({ url: url.href, downloadUrl: button.dataset.docDownloadUrl, title: button.dataset.docTitle || 'Documento', contentType: type, fileId: button.dataset.docFileId, trigger: button });
    } catch (failure) {
      if (version !== revision || (failure as Error).name === 'AbortError') return;
      (window as any).ProfintUI?.toast?.((failure as Error).message, { type: 'error' });
    }
  });
  window.addEventListener('pagehide', () => { revision++; controller?.abort(); });
})();

// ----------------------------------------------------------------------------
// Nueva autorización — typeahead del DNI (empleados activos de la empresa) +
// resumen en vivo. Al elegir un empleado se precargan apellido/nombre/email/
// teléfono; igual se puede tipear un DNI nuevo que no esté en la lista.
// ----------------------------------------------------------------------------
function initAutorizacionForm(scope: ParentNode = document) {
  const esc = (s: string) => (s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
  const norm = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
  const toDdmmyyyy = (iso: string) => { const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : iso; };

  scope.querySelectorAll<HTMLFormElement>('form[data-autorizacion-form]').forEach((form) => {
    if (form.dataset.afInit) return;
    form.dataset.afInit = '1';

    const fld = (id: string) => form.querySelector<HTMLInputElement>('#' + id);

    // ── Resumen en vivo (reemplaza el binding Alpine muerto) ──
    const sums = new Map<string, HTMLElement>();
    form.querySelectorAll<HTMLElement>('[data-sum]').forEach((el) => sums.set(el.dataset.sum!, el));
    const v = (id: string) => (fld(id)?.value || '').trim();
    const setSum = (k: string, val: string) => { const el = sums.get(k); if (el) el.textContent = val || '—'; };
    const syncAccidentCode = () => {
      const motivo = fld('Motivo');
      const codigo = fld('Codigo');
      if (!motivo || !codigo || motivo.dataset.accidentCodeSource !== 'true') return;
      const match = motivo.value.match(/\b(90|100|150)\b/);
      if (match?.[1] && codigo.value !== match[1]) {
        codigo.value = match[1];
        codigo.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };
    const syncSummary = () => {
      syncAccidentCode();
      setSum('empleado', [v('Apellido'), v('Nombre')].filter(Boolean).join(', '));
      setSum('dni', v('Dni'));
      setSum('codigo', v('Codigo') ? `Cód. ${v('Codigo')}` : '');
      setSum('motivo', v('Motivo'));
      setSum('fecha', v('Fecha') ? toDdmmyyyy(v('Fecha')) : '');
    };
    ['Dni', 'Apellido', 'Nombre', 'Motivo', 'Fecha', 'Codigo'].forEach((id) => {
      const el = fld(id);
      el?.addEventListener('input', syncSummary);
      el?.addEventListener('change', syncSummary);
    });
    syncSummary();

    // ── Typeahead del DNI ──
    const ta = form.querySelector<HTMLElement>('[data-empleado-ta]');
    const input = form.querySelector<HTMLInputElement>('[data-ta-input]');
    const menu = form.querySelector<HTMLElement>('[data-ta-menu]');
    if (!ta || !input || !menu) return;

    type Emp = { dni: string; apellido: string; nombre: string; email?: string; telefono?: string };
    let all: Emp[] | null = null, loading = false;

    const load = async () => {
      if (all || loading) return;
      loading = true;
      try { const r = await fetch(form.dataset.empleadosUrl!, { headers: { 'X-Requested-With': 'fetch' } }); all = r.ok ? await r.json() : []; }
      catch { all = []; }
      loading = false;
    };
    const close = () => { menu.hidden = true; ta.classList.remove('open'); document.removeEventListener('mousedown', onDoc); };
    const onDoc = (e: MouseEvent) => { if (!ta.contains(e.target as Node)) close(); };
    const setField = (id: string, val: string) => { const el = fld(id); if (el) { el.value = val; el.dispatchEvent(new Event('input', { bubbles: true })); } };
    const pick = (e: Emp) => {
      input.value = e.dni || '';
      setField('Apellido', e.apellido || '');
      setField('Nombre', e.nombre || '');
      setField('Email', e.email || '');
      setField('Telefono', e.telefono || '');
      syncSummary();
      close();
    };
    const render = () => {
      const raw = input.value.trim(), q = norm(raw);
      const list = (all || []).filter((e) =>
        !raw || (e.dni || '').startsWith(raw) || norm(`${e.apellido} ${e.nombre}`).includes(q)).slice(0, 8);
      if (list.length === 0) { close(); return; }
      menu.innerHTML = list.map((e, i) =>
        `<button type="button" class="rx-ta-opt" data-i="${i}"><span class="d">${esc(e.dni)}</span><span class="n">${esc(e.apellido)}, ${esc(e.nombre)}</span></button>`).join('');
      menu.querySelectorAll<HTMLButtonElement>('.rx-ta-opt').forEach((b) =>
        b.addEventListener('mousedown', (ev) => { ev.preventDefault(); pick(list[Number(b.dataset.i)]); }));
      menu.hidden = false; ta.classList.add('open');
      document.addEventListener('mousedown', onDoc);
    };

    input.addEventListener('focus', async () => { await load(); render(); });
    input.addEventListener('input', async () => { await load(); render(); });
    input.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  });
}
initAutorizacionForm();

// ----------------------------------------------------------------------------
// Command palette (⌘K / Ctrl+K) — navegación rápida client-side a secciones
// y acciones del portal. Sin backend.
// ----------------------------------------------------------------------------
(function initCommandPalette() {
  type Cmd = { label: string; href: string; icon: string; group: string; kw?: string };
  const CMDS: Cmd[] = [
    { group: 'Ir a', label: 'Inicio', href: '/portal', icon: 'ph-house', kw: 'dashboard home' },
    { group: 'Ir a', label: 'Bonos', href: '/bonos', icon: 'ph-receipt' },
    { group: 'Ir a', label: 'Turnos', href: '/turnos', icon: 'ph-calendar-check', kw: 'reservar' },
    { group: 'Ir a', label: 'Mis turnos', href: '/mis-turnos', icon: 'ph-calendar-dots' },
    { group: 'Ir a', label: 'Informes', href: '/informes', icon: 'ph-file-text' },
    { group: 'Ir a', label: 'Preocupacionales', href: '/preocupacionales', icon: 'ph-clipboard-text', kw: 'examenes' },
    { group: 'Ir a', label: 'Declaraciones Juradas', href: '/declaraciones-juradas', icon: 'ph-shield-check', kw: 'ddjj' },
    { group: 'Ir a', label: 'Cuenta corriente', href: '/cuenta-corriente', icon: 'ph-wallet', kw: 'saldo movimientos' },
    { group: 'Ir a', label: 'Empleados', href: '/pacientes', icon: 'ph-users', kw: 'asegurados personal pacientes' },
    { group: 'Ir a', label: 'Documentos', href: '/documentos', icon: 'ph-folder' },
    { group: 'Ir a', label: 'Mensajes', href: '/mensajes', icon: 'ph-chat-circle' },
    { group: 'Ir a', label: 'Manual de usuario', href: '/manual', icon: 'ph-book-open-text', kw: 'ayuda guia como usar portal inteligencia artificial chat asistente instrucciones' },
    { group: 'Ir a', label: 'Datos útiles', href: '/telefonos', icon: 'ph-phone', kw: 'contacto telefono' },
    { group: 'Ir a', label: 'Usuarios web', href: '/usuarios-web', icon: 'ph-users-three' },
    { group: 'Ir a', label: 'Mi perfil', href: '/manage', icon: 'ph-user-circle', kw: 'cuenta password contrasena' },
    { group: 'Acciones', label: 'Generar un bono nuevo', href: '/bonos/nuevo', icon: 'ph-plus-circle', kw: 'crear emitir' },
    { group: 'Acciones', label: 'Cambiar contraseña', href: '/manage/cambiar-password', icon: 'ph-lock-key' },
  ];

  let overlay: HTMLElement | null = null;
  let input: HTMLInputElement | null = null;
  let listEl: HTMLElement | null = null;
  let active = 0;
  let filtered: Cmd[] = [];

  function build() {
    overlay = document.createElement('div');
    overlay.className = 'rx-cmdk-overlay';
    overlay.innerHTML =
      '<div class="rx-cmdk" role="dialog" aria-label="Buscar">' +
      '<div class="rx-cmdk-search"><i class="ph ph-magnifying-glass"></i>' +
      '<input type="text" placeholder="Buscar secciones y acciones…" aria-label="Buscar" /><span class="rx-kbd">ESC</span></div>' +
      '<div class="rx-cmdk-list"></div>' +
      '<div class="rx-cmdk-foot"><span><b>↑↓</b> navegar</span><span><b>↵</b> abrir</span><span><b>esc</b> cerrar</span></div>' +
      '</div>';
    document.body.appendChild(overlay);
    input = overlay.querySelector('input');
    listEl = overlay.querySelector('.rx-cmdk-list');
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    input!.addEventListener('input', () => { active = 0; render(); });
    input!.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(active + 1, filtered.length - 1); render(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(active - 1, 0); render(); }
      else if (e.key === 'Enter') { e.preventDefault(); if (filtered[active]) go(filtered[active]); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); }
    });
  }
  function go(c: Cmd) { window.location.href = c.href; }
  function render() {
    const q = (input!.value || '').trim().toLowerCase();
    filtered = CMDS.filter((c) => !q || (c.label + ' ' + (c.kw || '')).toLowerCase().includes(q));
    if (!filtered.length) { listEl!.innerHTML = '<div class="rx-cmdk-empty">Sin resultados para "' + q + '"</div>'; return; }
    let html = ''; let lastGroup = '';
    filtered.forEach((c, i) => {
      if (c.group !== lastGroup) { html += `<div class="rx-cmdk-group">${c.group}</div>`; lastGroup = c.group; }
      html += `<div class="rx-cmdk-item${i === active ? ' active' : ''}" data-i="${i}"><span class="ico"><i class="ph ${c.icon}"></i></span><span>${c.label}</span><span class="arr"><i class="ph ph-arrow-right"></i></span></div>`;
    });
    listEl!.innerHTML = html;
    listEl!.querySelectorAll<HTMLElement>('.rx-cmdk-item').forEach((el) => {
      el.addEventListener('mousemove', () => { active = Number(el.dataset.i); paintActive(); });
      el.addEventListener('click', () => go(filtered[Number(el.dataset.i)]));
    });
    const act = listEl!.querySelector('.rx-cmdk-item.active') as HTMLElement | null;
    act?.scrollIntoView({ block: 'nearest' });
  }
  function paintActive() {
    listEl!.querySelectorAll<HTMLElement>('.rx-cmdk-item').forEach((el) => el.classList.toggle('active', Number(el.dataset.i) === active));
  }
  function open() { if (!overlay) build(); overlay!.style.display = 'flex'; input!.value = ''; active = 0; render(); input!.focus(); }
  function close() { if (overlay) overlay.style.display = 'none'; }

  window.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); (overlay && overlay.style.display === 'flex') ? close() : open(); }
  });
  // El buscador del topbar también abre la paleta.
  document.querySelectorAll<HTMLInputElement>('.rx-search input').forEach((el) => {
    el.addEventListener('focus', (ev) => { (ev.target as HTMLInputElement).blur(); open(); });
  });
})();

// ----------------------------------------------------------------------------
// Password reveal — disponible en los accesos de clientes y médicos.
// ----------------------------------------------------------------------------
// Existing password markup is adopted by the shared text/access controller.
